import React, { useRef, useState } from "react";
import OverflowTooltip from "./OverflowTooltip";
import useFitCount from "./useFitCount";

/**
 * A list of answers on one line, with a count standing for whatever did not
 * fit — the read-only twin of `MultiSelect`'s chip row, so a field that takes
 * several answers reads the same where it is entered and where it is shown
 * back. A client's goals and their food allergies are both drawn with this.
 *
 * One line and not two: these sit in rows of three fields, and a value that
 * wrapped would push its card taller than the ones beside it for the sake of a
 * client who reacts to four things rather than one.
 *
 * Plain names rather than the form's chips: nothing here is removable, and
 * pills around read-only text would offer a control that is not there.
 *
 * It measures itself against the cell it is in, so whatever contains it needs
 * `min-w-0` — given its content's width instead it would never overflow, and
 * so would never count anything.
 */

/** `gap-1` between the names, in px — the measuring pass has to know it. */
const NAME_GAP = 4;

export interface OneLineListProps {
  items: string[];
  /**
   * What the answers are, plural, for the heading on the hover box: "3 goals".
   * Read only when something is actually behind the count.
   */
  label: string;
  /**
   * A ceiling on how many names are drawn, whatever else the line has room for
   * — for a column narrow enough that a long list crowds the ones beside it.
   *
   * A ceiling only: names are still shown whole or not at all, so a line under
   * it may draw fewer, and everything not drawn is behind the count either way.
   */
  max?: number;
}

const OneLineList: React.FC<OneLineListProps> = ({ items, label, max }) => {
  const rowRef = useRef<HTMLSpanElement>(null);
  const itemRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const overflowRef = useRef<HTMLSpanElement>(null);

  // Only the ones the ceiling allows are measured — the rest are behind the
  // count however the line comes out.
  const capped = max === undefined ? items.length : Math.min(items.length, max);

  const key = `${items.join("|")}|${capped}`;
  const { measuring, shown, tight } = useFitCount({
    rowRef,
    itemRefs,
    overflowRef,
    key,
    count: capped,
    gap: NAME_GAP,
    // Where the ceiling already holds names back, the pill is shown whatever
    // fits, so the last name measured has to leave room for it too.
    hasMore: capped < items.length,
  });

  // Hovering the line lists every answer, once some of them are behind the
  // count — the same box the form's picker opens, so the answers are read the
  // same way where they are entered and where they are shown back.
  const [hovered, setHovered] = useState(false);

  if (items.length === 0) return <>-</>;

  // The measuring pass lays out everything under the ceiling; the one after it
  // draws whatever fit whole.
  const drawn = items.slice(0, measuring ? capped : shown);
  const hiddenCount = measuring ? 0 : items.length - shown;

  return (
    <span
      ref={rowRef}
      data-fit-row=""
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`flex items-baseline gap-1 overflow-hidden ${
        measuring ? "invisible" : ""
      }`}
    >
      {drawn.map((name, index) => (
        <span
          key={`${name}-${index}`}
          ref={(node) => {
            itemRefs.current[index] = node;
          }}
          data-fit-item=""
          className={tight ? "truncate" : "flex-shrink-0 whitespace-nowrap"}
        >
          {/* The separator belongs to the name before it, so it is measured
              with it — and the last one drawn goes without, since nothing
              follows it but the count. */}
          {index < drawn.length - 1 ? `${name},` : name}
        </span>
      ))}

      {/* Rendered through the measuring pass at its widest, so the space kept
          for it is enough for the number it ends up showing. */}
      {(measuring ? items.length > 1 : hiddenCount > 0) && (
        <span
          ref={overflowRef}
          data-fit-overflow=""
          className="inline-flex flex-shrink-0 items-center rounded-full bg-[#F1F4F6] px-2 py-0.5 text-[13px] font-medium text-ink-muted"
        >
          +{measuring ? items.length - 1 : hiddenCount}
          <span className="sr-only"> more: {items.join(", ")}</span>
        </span>
      )}

      {/* Only where something is actually behind the count: a box repeating a
          line that is already whole on screen is noise under the pointer. */}
      <OverflowTooltip
        anchorRef={rowRef}
        open={hovered && hiddenCount > 0 && !measuring}
        items={items}
        heading={`${items.length} ${label}`}
      />
    </span>
  );
};

export default OneLineList;
